import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, ArrowRight, ShieldCheck, Sparkle, CurrencyEur, Heart, Globe, Lightning, ChatCircleDots, Trophy, ChartLine, CaretDown } from "@phosphor-icons/react";

export default function PourquoiPartenaire() {
  return (
    <div data-testid="pourquoi-partenaire">
      <Hero />
      <Section2Avantages />
      <Section3Comparatif />
      <Section4Premium />
      <Section5PremiumAI />
      <Section6Visibilite />
      <Section7Temoignages />
      <Section8Essai />
      <Section9FAQ />
    </div>
  );
}

function Hero() {
  return (
    <section className="bg-primary text-primary-foreground" data-testid="pp-hero">
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-20 md:py-28">
        <div className="label-uppercase mb-4 opacity-70" style={{ color: "#F9F8F6" }}>Espace partenaires</div>
        <h1 className="font-display text-3xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05] max-w-3xl">
          Développez la visibilité de votre entreprise dans le Haut-Rhin
        </h1>
        <p className="mt-6 text-base sm:text-lg max-w-2xl opacity-90 leading-relaxed">
          {"MonCommerce68 permet aux entreprises locales d'améliorer leur présence en ligne, d'être davantage visibles auprès des particuliers et professionnels, et de bénéficier d'outils modernes pour développer leur activité."}
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/inscription" className="bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-5 py-3 rounded-sm text-sm font-semibold inline-flex items-center gap-2" data-testid="pp-cta-essai">
            Essayer gratuitement pendant 14 jours <ArrowRight size={14}/>
          </Link>
          <a href="#offres" className="border border-primary-foreground/40 hover:bg-primary-foreground/10 px-5 py-3 rounded-sm text-sm font-semibold" data-testid="pp-cta-offres">
            Voir les offres
          </a>
        </div>
      </div>
    </section>
  );
}

function Section2Avantages() {
  const items = [
    "Visibilité locale renforcée",
    "Présence sur une plateforme dédiée aux entreprises du Haut-Rhin",
    "Mise en relation avec de nouveaux clients",
    "Référencement complémentaire à votre site internet",
    "Présence consultable 24h/24 et 7j/7",
    "Valorisation de votre activité, produits et services",
    "Visibilité auprès d'un public français, allemand et international",
  ];
  return (
    <section className="border-b border-border" data-testid="pp-section-avantages">
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16">
        <div className="label-uppercase mb-3">Pourquoi être présent ?</div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mb-8 max-w-3xl">
          Pourquoi être présent sur MonCommerce68 ?
        </h2>
        <ul className="grid sm:grid-cols-2 gap-3 max-w-3xl">
          {items.map((txt, i) => (
            <li key={i} className="flex items-start gap-2 text-sm sm:text-base">
              <Check size={16} weight="bold" className="text-primary mt-1 shrink-0"/>
              <span>{txt}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

const FEATURES = [
  ["Nom entreprise",                     true, true, true],
  ["Adresse",                            true, true, true],
  ["Téléphone",                          true, true, true],
  ["Site internet",                      true, true, true],
  ["Logo entreprise",                    false, true, true],
  ["Galerie photos",                     false, true, true],
  ["Description détaillée",              false, true, true],
  ["Badge Partenaire MonCommerce68",     false, true, true],
  ["Mise en avant dans les résultats",   false, true, true],
  ["Priorité d'affichage",               false, true, true],
  ["WhatsApp direct",                    false, true, true],
  ["Publication d'actualités",           false, true, true],
  ["Présentation enrichie",              false, true, true],
  ["Optimisation IA du contenu",         false, false, true],
  ["Aide à la rédaction IA",             false, false, true],
  ["Suggestions marketing IA",           false, false, true],
  ["Contenus améliorés automatiquement", false, false, true],
  ["Outils IA avancés",                  false, false, true],
];

function Cell({ on }) {
  return on
    ? <span className="inline-flex items-center justify-center w-6 h-6 bg-primary/10 text-primary rounded-full"><Check size={14} weight="bold"/></span>
    : <span className="inline-flex items-center justify-center w-6 h-6 text-muted-foreground/40"><X size={14}/></span>;
}

function Section3Comparatif() {
  return (
    <section className="bg-accent/20 border-b border-border" id="offres" data-testid="pp-section-comparatif">
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16">
        <div className="label-uppercase mb-3">Comparatif</div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mb-8 max-w-3xl">
          {"Choisissez l'offre adaptée à vos besoins"}
        </h2>
        <div className="overflow-x-auto bg-card border border-border rounded-sm">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-border bg-background">
                <th className="px-4 py-4 text-left font-display font-medium">Fonctionnalité</th>
                <th className="px-4 py-4 text-center font-display font-medium w-32">Gratuit</th>
                <th className="px-4 py-4 text-center font-display font-medium w-32">
                  <span className="block text-primary">Premium</span>
                </th>
                <th className="px-4 py-4 text-center font-display font-medium w-32">
                  <span className="block text-secondary">Premium IA</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {FEATURES.map(([label, free, premium, premiumAi], i) => (
                <tr key={i} className="hover:bg-accent/30">
                  <td className="px-4 py-2.5">{label}</td>
                  <td className="px-4 py-2.5 text-center"><Cell on={free}/></td>
                  <td className="px-4 py-2.5 text-center"><Cell on={premium}/></td>
                  <td className="px-4 py-2.5 text-center"><Cell on={premiumAi}/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link to="/inscription" className="bg-primary text-primary-foreground hover:bg-[#0f3d29] hover:text-white px-5 py-3 rounded-sm text-sm font-semibold inline-flex items-center gap-2" data-testid="pp-cta-comparatif">
            Démarrer mon essai 14 jours <ArrowRight size={14}/>
          </Link>
        </div>
      </div>
    </section>
  );
}

function FeatureIcon({ icon, label }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 shrink-0 bg-primary/10 text-primary rounded-sm grid place-items-center">{icon}</div>
      <div className="text-sm font-display font-semibold pt-2">{label}</div>
    </div>
  );
}

function Section4Premium() {
  return (
    <section className="border-b border-border" data-testid="pp-section-premium">
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16">
        <div className="label-uppercase mb-3 text-primary">Premium</div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mb-4 max-w-3xl">Pourquoi choisir Premium ?</h2>
        <p className="text-base text-muted-foreground max-w-3xl leading-relaxed mb-8">
          {"Le compte Premium permet à votre entreprise de se démarquer grâce à une présentation complète, professionnelle et attractive. Grâce au logo, aux photos, aux descriptions détaillées et à la mise en avant dans les résultats de recherche, votre entreprise bénéficie d'une meilleure visibilité auprès des visiteurs de la plateforme."}
        </p>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl">
          <FeatureIcon icon={<Globe size={20} weight="duotone"/>} label="Plus visible"/>
          <FeatureIcon icon={<ShieldCheck size={20} weight="duotone"/>} label="Plus professionnel"/>
          <FeatureIcon icon={<ChatCircleDots size={20} weight="duotone"/>} label="Plus de contacts"/>
          <FeatureIcon icon={<Trophy size={20} weight="duotone"/>} label="Plus de crédibilité"/>
        </div>
      </div>
    </section>
  );
}

function Section5PremiumAI() {
  return (
    <section className="bg-secondary/5 border-b border-border" data-testid="pp-section-premium-ai">
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16">
        <div className="label-uppercase mb-3 text-secondary">Premium IA</div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mb-4 max-w-3xl">Pourquoi choisir Premium IA ?</h2>
        <p className="text-base text-muted-foreground max-w-3xl leading-relaxed mb-6">
          {"Premium IA inclut tous les avantages Premium ainsi que des outils d'intelligence artificielle destinés à améliorer votre présence numérique. L'intelligence artificielle peut vous aider à :"}
        </p>
        <ul className="space-y-2 max-w-2xl mb-8">
          {[
            "améliorer vos descriptions",
            "optimiser vos textes",
            "mettre en valeur vos services",
            "générer des contenus plus attractifs",
            "améliorer votre communication digitale",
          ].map((txt, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <Sparkle size={14} weight="fill" className="text-secondary mt-1 shrink-0"/>
              <span>{txt}</span>
            </li>
          ))}
        </ul>
        <div className="bg-card border-l-4 border-secondary rounded-sm p-4 max-w-3xl">
          <div className="text-sm italic text-foreground">
            « Les outils IA sont conçus pour vous faire gagner du temps et renforcer votre visibilité en ligne. »
          </div>
        </div>
      </div>
    </section>
  );
}

function Section6Visibilite() {
  return (
    <section className="border-b border-border" data-testid="pp-section-visibilite">
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16">
        <div className="label-uppercase mb-3">Visibilité &amp; référencement</div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mb-4 max-w-3xl">
          Comment MonCommerce68 améliore votre présence en ligne
        </h2>
        <p className="text-base text-muted-foreground max-w-3xl leading-relaxed mb-6">
          {"MonCommerce68 permet à votre entreprise d'être présente sur une plateforme spécialisée dédiée aux entreprises du Haut-Rhin. Une fiche entreprise complète avec photos, descriptions et informations actualisées peut contribuer à renforcer votre visibilité numérique et à faciliter votre découverte par de nouveaux clients."}
        </p>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mt-8">
          {[
            { icon: <ChartLine size={20} weight="duotone"/>, label: "Potentiel d'amélioration de visibilité" },
            { icon: <Lightning size={20} weight="duotone"/>, label: "Présence renforcée" },
            { icon: <Globe size={20} weight="duotone"/>, label: "Visibilité locale accrue" },
            { icon: <Heart size={20} weight="duotone"/>, label: "Meilleure présence numérique" },
          ].map((x, i) => <FeatureIcon key={i} icon={x.icon} label={x.label}/>)}
        </div>
      </div>
    </section>
  );
}

function Section7Temoignages() {
  return (
    <section className="bg-accent/10 border-b border-border" data-testid="pp-section-temoignages">
      <div className="mx-auto max-w-6xl px-6 lg:px-10 py-16">
        <div className="label-uppercase mb-3">Témoignages</div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mb-4">Ce que disent nos partenaires</h2>
        <div className="bg-card border border-dashed border-border rounded-sm p-10 text-center max-w-2xl">
          <Trophy size={28} className="mx-auto text-muted-foreground mb-3" weight="duotone"/>
          <div className="text-sm font-display font-semibold mb-2">Les premiers témoignages arrivent bientôt</div>
          <p className="text-sm text-muted-foreground">
            Nous collectons les retours de nos premiers partenaires Premium. Soyez parmi les premiers à témoigner — un mois offert à toute entreprise qui partage son expérience.
          </p>
        </div>
      </div>
    </section>
  );
}

function Section8Essai() {
  return (
    <section className="bg-primary text-primary-foreground" data-testid="pp-section-essai">
      <div className="mx-auto max-w-4xl px-6 lg:px-10 py-20 text-center">
        <CurrencyEur size={36} className="mx-auto mb-4 opacity-70" weight="duotone"/>
        <h2 className="font-display text-3xl sm:text-5xl tracking-tighter font-medium mb-4">
          Essayez MonCommerce68 gratuitement pendant 14 jours
        </h2>
        <p className="text-base sm:text-lg max-w-2xl mx-auto opacity-90 leading-relaxed mb-8">
          {"Découvrez tous les avantages de l'abonnement Premium ou Premium IA sans engagement pendant 14 jours."}
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/inscription" className="bg-secondary text-secondary-foreground hover:bg-[#b56847] hover:text-white px-6 py-3.5 rounded-sm text-sm font-semibold inline-flex items-center gap-2" data-testid="pp-cta-essai-bottom">
            Commencer mon essai gratuit <ArrowRight size={14}/>
          </Link>
          <Link to="/passez-partenaire" className="border border-primary-foreground/40 hover:bg-primary-foreground/10 px-6 py-3.5 rounded-sm text-sm font-semibold">
            Devenir partenaire
          </Link>
        </div>
        <div className="mt-6 text-sm opacity-80">Aucune carte bancaire demandée · Annulation en 1 clic</div>
      </div>
    </section>
  );
}

const FAQ = [
  { q: "Pourquoi devenir partenaire ?", a: "Devenir partenaire MonCommerce68 vous permet d'enrichir votre fiche entreprise (logo, photos, descriptions), d'être mis en avant dans les résultats de recherche, et de bénéficier d'outils modernes pour développer votre activité auprès des habitants du Haut-Rhin." },
  { q: "Comment fonctionne l'essai gratuit ?", a: "Vous bénéficiez de 14 jours d'essai gratuit avec tous les avantages Premium ou Premium IA, sans carte bancaire demandée. À l'issue de ces 14 jours, vous choisissez librement de poursuivre l'abonnement payant ou de revenir à la version gratuite." },
  { q: "Puis-je arrêter à tout moment ?", a: "Oui, vous pouvez résilier votre abonnement en un seul clic depuis votre espace partenaire, à tout moment et sans frais. Aucun engagement de durée." },
  { q: "Quelle différence entre Premium et Premium IA ?", a: "Premium vous donne accès à la fiche enrichie complète : logo, galerie photos, description, badge partenaire, mise en avant. Premium IA inclut tous les avantages Premium et y ajoute des outils d'intelligence artificielle pour optimiser automatiquement vos descriptions, suggérer des contenus marketing et améliorer votre communication digitale." },
  { q: "Comment modifier ma fiche ?", a: "Connectez-vous à votre espace partenaire pour éditer en direct toutes les informations de votre fiche : coordonnées, photos, description, horaires. Les modifications sont visibles immédiatement sur le site." },
  { q: "Comment améliorer ma visibilité ?", a: "Complétez votre fiche à 100% (logo, photos, description, horaires), ajoutez vos coordonnées WhatsApp, publiez régulièrement des actualités, et utilisez les outils IA Premium pour optimiser le contenu. Plus votre fiche est complète et active, plus elle est mise en avant." },
];

function Section9FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section className="border-b border-border" data-testid="pp-section-faq">
      <div className="mx-auto max-w-3xl px-6 lg:px-10 py-16">
        <div className="label-uppercase mb-3">FAQ</div>
        <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mb-8">Questions fréquentes</h2>
        <div className="divide-y divide-border border-y border-border">
          {FAQ.map((f, i) => (
            <div key={i}>
              <button onClick={() => setOpen(open === i ? -1 : i)} className="w-full py-4 flex items-center justify-between text-left hover:text-primary transition-colors" data-testid={`pp-faq-${i}`}>
                <span className="font-display font-medium pr-4">{f.q}</span>
                <CaretDown size={16} className={`shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`}/>
              </button>
              {open === i && <div className="pb-4 text-sm text-muted-foreground leading-relaxed">{f.a}</div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
